import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import type { Language, TranslationKey } from './translations';
import { fr, loadDictionary, AVAILABLE_LANGUAGES } from './locales';
import type { Dictionary } from './locales/types';
import { LANGUAGE_INFO, isLanguage } from './languages';
import { installApiLanguageHeader } from './apiLanguage';
import { applyChartLanguage } from './chartLanguage';

export type { Language, TranslationKey } from './translations';
export { LANGUAGE_INFO } from './languages';

type Vars = Record<string, string | number>;

interface I18nContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: TranslationKey, vars?: Vars) => string;
  /** Pluriel : choisit `<base>_one` ou `<base>_other` selon n, et remplace {n}. */
  tp: (base: string, n: number) => string;
  formatCO2Value: (kg: number) => string;
  formatNumber: (num: number, decimals?: number) => string;
  formatCurrency: (num: number) => string;
  /** Locale pour toLocaleString / Intl (fr-FR, en-GB, hi-IN…). */
  locale: string;
}

const I18nContext = createContext<I18nContextType | null>(null);

const STORAGE_KEY = 'wattel-language';
const getLocale = (lang: Language): string => LANGUAGE_INFO[lang]?.locale ?? 'fr-FR';

/** Langues proposées : celles dont les fichiers de traduction existent et qui sont déclarées dans le registre. */
export const SUPPORTED_LANGUAGES: Language[] = AVAILABLE_LANGUAGES.filter(isLanguage);

/** Langue enregistrée par l'utilisateur (français par défaut). */
export function savedLanguage(): Language {
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    return isLanguage(s) && SUPPORTED_LANGUAGES.includes(s) ? s : 'fr';
  } catch { return 'fr'; }
}

// Dictionnaires chargés : le français est embarqué, les autres langues arrivent à la demande (fichiers JSON).
const dictionaries: Partial<Record<Language, Dictionary>> = { fr };

/** Charge les traductions d'une langue si ce n'est pas déjà fait (avant le premier affichage et à chaque changement). */
export async function preloadLanguage(lang: Language): Promise<void> {
  if (!dictionaries[lang]) dictionaries[lang] = await loadDictionary(lang);
}

function lookup(lang: Language, key: string, vars?: Vars): string {
  let text = dictionaries[lang]?.[key] || fr[key] || key;
  if (vars) for (const [k, v] of Object.entries(vars)) text = text.split(`{${k}}`).join(String(v));
  return text;
}

// Langue courante, lisible hors composant React (utilitaires, services, en-tête Accept-Language).
let currentLanguage: Language = savedLanguage();

/** Traduction hors composant (fonctions utilitaires, services). Dans un composant, utiliser useI18n().t. */
export function translate(key: TranslationKey, vars?: Vars): string {
  return lookup(currentLanguage, key, vars);
}

/** Locale courante hors composant. */
export function currentLocale(): string { return getLocale(currentLanguage); }

/** Langue courante hors composant. */
export function getCurrentLanguage(): Language { return currentLanguage; }

/** Traduction dans une langue précise (sa traduction doit avoir été chargée par preloadLanguage). */
export function translateIn(lang: Language, key: TranslationKey, vars?: Vars): string { return lookup(lang, key, vars); }

/** Locale d'une langue précise. */
export function localeOf(lang: Language): string { return getLocale(lang); }

// Toutes les requêtes vers les API ZOS portent la langue choisie (en-tête Accept-Language).
installApiLanguageHeader(() => currentLanguage);

// CO₂ : kg sous 1000, tonnes au-delà, au format de la langue.
export function formatCO2(kg: number, lang: Language = 'fr'): string {
  const locale = getLocale(lang);
  if (kg >= 1000) return `${(kg / 1000).toLocaleString(locale, { maximumFractionDigits: 2 })} t`;
  return `${kg.toLocaleString(locale, { maximumFractionDigits: 1 })} kg`;
}

/** Charge (une seule fois) la police d'une écriture non latine, ex. le devanagari pour l'hindi. */
function ensureFont(lang: Language) {
  const href = LANGUAGE_INFO[lang]?.fontStylesheet;
  if (!href || document.querySelector(`link[data-i18n-font="${lang}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet'; link.href = href; link.dataset.i18nFont = lang;
  document.head.appendChild(link);
}

export function I18nProvider({ children }: { children: ReactNode }) {
  // La langue enregistrée a été préchargée avant l'affichage (main.tsx) ; sinon le français sert de secours.
  const [language, setLanguageState] = useState<Language>(() => (dictionaries[currentLanguage] ? currentLanguage : 'fr'));

  // Change de langue une fois ses traductions chargées : l'interface bascule d'un coup, sans état intermédiaire.
  const setLanguage = useCallback((lang: Language) => {
    preloadLanguage(lang).then(() => {
      currentLanguage = lang; // avant le rendu : translate() / currentLocale() / Accept-Language suivent tout de suite
      try { localStorage.setItem(STORAGE_KEY, lang); } catch { /* stockage indisponible */ }
      setLanguageState(lang);
    }).catch(err => console.error('[i18n] chargement de la langue impossible :', lang, err));
  }, []);

  // Synchronisé pendant le rendu, avant les enfants qui appellent translate().
  currentLanguage = language;

  const t = useCallback((key: TranslationKey, vars?: Vars): string => lookup(language, key, vars), [language]);

  const tp = useCallback((base: string, n: number): string => {
    const rule = new Intl.PluralRules(getLocale(language)).select(n);
    return t(`${base}_${rule === 'one' ? 'one' : 'other'}` as TranslationKey, { n });
  }, [language, t]);

  // attribut lang du document (lecteurs d'écran, césure, traduction du navigateur, police par écriture) + police dédiée
  useEffect(() => {
    document.documentElement.lang = language;
    ensureFont(language);
  }, [language]);

  // Graphiques Highcharts : mois, jours et séparateurs décimaux dans la langue choisie,
  // appliqués pendant le rendu pour que le premier dessin des graphiques enfants soit déjà dans la bonne langue.
  const chartLang = useRef<Language | null>(null);
  if (chartLang.current !== language) { applyChartLanguage(language); chartLang.current = language; }

  const locale = getLocale(language);

  const formatNumber = useCallback((num: number, decimals = 0): string =>
    num.toLocaleString(locale, { maximumFractionDigits: decimals, minimumFractionDigits: decimals > 0 ? 1 : 0 }), [locale]);

  const formatCurrency = useCallback((num: number): string =>
    num.toLocaleString(locale, { maximumFractionDigits: 2, minimumFractionDigits: 2 }) + ' €', [locale]);

  const formatCO2Value = useCallback((kg: number): string => formatCO2(kg, language), [language]);

  return (
    <I18nContext.Provider value={{ language, setLanguage, t, tp, formatCO2Value, formatNumber, formatCurrency, locale }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n must be used within an I18nProvider');
  return context;
}

// ─── Sélecteurs de langue ───

/** Rangée de drapeaux (ancien tableau de démonstration). */
export function LanguageSelector({ className = '' }: { className?: string }) {
  const { language, setLanguage } = useI18n();
  return (
    <div className={`flex items-center gap-1 ${className}`}>
      {SUPPORTED_LANGUAGES.map(code => {
        const { Flag, label } = LANGUAGE_INFO[code];
        return (
          <button
            key={code}
            onClick={() => setLanguage(code)}
            className={`px-2 py-1.5 rounded-lg transition-all ${language === code ? 'bg-white shadow-sm' : 'opacity-60 hover:opacity-100 hover:bg-white/10'}`}
            title={label}
            aria-label={label}
            aria-pressed={language === code}
          >
            <Flag className="h-3.5 w-5 rounded-[2px] ring-1 ring-black/10" />
          </button>
        );
      })}
    </div>
  );
}

/** Sélecteur compact : drapeau + code, menu des langues. `tone` : 'light' (header blanc) ou 'dark' (fond bleu). */
export function LanguageSwitcher({ tone = 'light', className = '' }: { tone?: 'light' | 'dark'; className?: string }) {
  const { language, setLanguage, t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current = LANGUAGE_INFO[language];

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown); document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  if (SUPPORTED_LANGUAGES.length < 2) return null;

  const btn = tone === 'dark'
    ? 'bg-white/10 hover:bg-white/20 border-white/25 text-white backdrop-blur-xl'
    : 'bg-white hover:bg-gray-50 border-gray-200 text-gray-700';

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${t('lang_label')} : ${current.label}`}
        title={t('lang_label')}
        className={`inline-flex items-center gap-1.5 rounded-lg border px-2 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors ${btn}`}
      >
        <current.Flag className="h-3.5 w-5 rounded-[2px] shadow-sm ring-1 ring-black/10" />
        {current.code}
        <svg className={`h-3 w-3 opacity-70 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" /></svg>
      </button>
      {open && (
        <ul role="listbox" className="absolute right-0 z-[300] mt-1.5 min-w-[160px] overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-xl">
          {SUPPORTED_LANGUAGES.map(code => {
            const { Flag, label } = LANGUAGE_INFO[code];
            return (
              <li key={code}>
                <button
                  type="button"
                  role="option"
                  lang={code}
                  aria-selected={language === code}
                  onClick={() => { setLanguage(code); setOpen(false); }}
                  className={`flex w-full items-center gap-2.5 px-3 py-2 text-sm transition-colors ${language === code ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`}
                >
                  <Flag className="h-3.5 w-5 rounded-[2px] shadow-sm ring-1 ring-black/10" />
                  <span className="flex-1 text-left">{label}</span>
                  {language === code && <svg className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
